import React from "react";
import { fileToImageDataArray as getpdfData } from "pdf-utils";
import {
  fileToImageData as getImageData,
  resizeImageData,
  imageDataIsLandscape,
  urlToimage,
  rotateImageData
} from "image-utils";
import { drawRectangle, writeText } from "canvas-utils";

import format from "date-fns/format";

import { websiteService, pageService } from "ajax";

import { normalize, schema } from "normalizr";
import axios from "axios";

export const PAGE_LIMIT = 10;

export function getPaperworkDims() {
  /*
    (P = padding) (S = Safe area) all co-ords worked out from origin (O)
    O -----------------------------------------------------------------
    | S P P P P P P P P P P P P P P P P P P P P P P P P S P P P P P P |
    | S (Font size --------------------------Font size) S --------- P |
    | S P P P P P P P P P P P P P P P P P P P P P P P P S |       | P |
    | S                                                 S |       | P |
    | S                     [barcode]                   S | logo  | P |
    | S                                                 S |       | P |
    | S P P P P P P P P P P P P P P P P P P P P P P P P S |       | P |
    | S (Font size -------------------------Font size)  S _________ P |
    | S P P P P P P P P P P P P P P P P P P P P P P P P S P P P P P P |
    -------------------------------------------------------------------
  */
  let dims = {
    //all in mm
    page: {
      width: 210,
      height: 297
    },
    barcodeMargin: 10, // margin around page that label cannot be placed (in mm)
    barcode: {
      width: 58,
      height: 10
    },
    padding: 0.8,
    safeArea: 2,
    fontSize: 2,
    logoWidth: 15.6 / (220 / 91) // logoHeight / (220/91) = 6.457
  };
  dims.label = {
    width:
      dims.barcode.width + dims.logoWidth + dims.padding + dims.safeArea * 2,
    height: dims.barcode.height + dims.fontSize * 2 + dims.padding * 4
  };
  dims.outerWidth =
    dims.safeArea * 2 + dims.barcode.width + dims.logoWidth + dims.padding;
  dims.outerHeight = dims.padding * 4 + dims.barcode.height + dims.fontSize * 2;
  return dims;
}

//1169 x 827  = A4 @ 100dpi
//1754 x 1240 = A4 @ 150dpi
//2339 x 1653 = A4 @ 200dpi
//3508 x 2480 = A4 @ 300dpi

const WIDTH = 1240;
const HEIGHT = 1754;

export function processFile(file) {
  var ext = file.name
    .split(".")
    .pop()
    .toLowerCase();
  if (ext === "pdf") {
    return getpdfData(file, WIDTH, HEIGHT);
  } else {
    return getImageData(file).then(function(image) {
      return imageDataIsLandscape(image).then(function(landscape) {
        // resize treating height as longest side
        var w = WIDTH;
        var h = HEIGHT;
        if (landscape) {
          [w, h] = [h, w]; //swap if landscape
        }

        return resizeImageData(image, w, h)
          .then(function(image) {
            if (landscape) {
              return rotateImageData(image, 90);
            } else {
              return image;
            }
          })
          .then(function(image) {
            return [image]; //wrap in array to match pdf output
          });
      });
    });
  }
}

export function processFiles(files) {
  let promises = [...files].map(file => processFile(file));
  return Promise.all(promises).then(function(arrs) {
    // flatten array of arrays
    return arrs.reduce((arr, val) => [...arr, ...val], []);
  });
}

export function uploadPaperwork(imageData, pageNo, xPos, yPos) {
  //ensure positive vals
  xPos = xPos < 0 ? 0 : xPos;
  yPos = yPos < 0 ? 0 : yPos;

  //convert decimals to 0-255 (byte)
  xPos = Math.round(255 * xPos);
  yPos = Math.round(255 * yPos);

  return pageService("uploadCustomerPaperwork", {
    imageData,
    pageNo,
    xPos,
    yPos
  });
}

export function uploadAndAssignPaperwork(
  barcode,
  imageData,
  pageNo,
  xPos,
  yPos
) {
  //ensure positive vals
  xPos = xPos < 0 ? 0 : xPos;
  yPos = yPos < 0 ? 0 : yPos;

  //convert decimals to 0-255 (byte)
  xPos = Math.round(255 * xPos);
  yPos = Math.round(255 * yPos);

  return pageService("uploadAndAssignCustomerPaperwork", {
    barcode,
    imageData,
    pageNo,
    xPos,
    yPos
  });
}

export function deletePaperwork(pwkIds) {
  return websiteService("deleteCustomerPaperwork", { pwkIds });
}

export function getPaperworkForRun(runNo) {
  return websiteService("getPaperworkForRun", { runNo: runNo }).then(function(
    data
  ) {
    // convert 0-255 val to decimal
    return data.map(function(pwk) {
      pwk.xPos = pwk.xPos / 255;
      pwk.yPos = pwk.yPos / 255;
      return pwk;
    });
  });
}

// gets all cons (& images) for a given customer & date
export function getConsAndServerImages_cust(despatchDate, customer) {
  return pageService("getConsAndPaperwork_cust", {
    customer,
    despatchDate
  }).then(normalizeConsAndServerImages);
}

// gets all cons (& images) for "defaultDepot" & date (all customer)
export function getConsAndServerImages_depot(despatchDate) {
  return pageService("getConsAndPaperwork_depot", { despatchDate }).then(
    normalizeConsAndServerImages
  );
}

export function normalizeConsAndServerImages(data) {
  const serverImage = new schema.Entity(
    "serverImages",
    {},
    {
      processStrategy: function(value, parent, key) {
        let newVal = Object.assign({}, value); //dont mutate
        //generate url
        newVal.url = getPaperworkURL(newVal.id);
        // convert 0-255 to 0-1
        newVal.xPos = newVal.xPos / 255;
        newVal.yPos = newVal.yPos / 255;
        return newVal;
      }
    }
  );
  const consignment = new schema.Entity(
    "consignments",
    {
      serverImages: [serverImage]
    },
    {
      idAttribute: "barcode",
      processStrategy: function(value, parent, key) {
        let newVal = Object.assign({}, value); //dont mutate
        // uppercase the postcode
        newVal.postcode = newVal.postcode.toUpperCase();
        // rename paperwork to serverImages
        newVal.serverImages = newVal.paperwork;
        delete newVal.paperwork;
        // add local images property
        newVal.localImages = [];
        return newVal;
      }
    }
  );
  return normalize(data, [consignment]).entities; // just return entities
}

// takes an array of con objects and sorts by conNo (ignoring case)
export function sortConsByConNo(cons) {
  return [...cons].sort(function(a, b) {
    var conNoA = a.conNo.toUpperCase(); // ignore upper and lowercase
    var conNoB = b.conNo.toUpperCase(); // ignore upper and lowercase
    if (conNoA < conNoB) {
      return -1;
    }
    if (conNoA > conNoB) {
      return 1;
    }
    return 0;
  });
}

export function getPaperworkSettings() {
  let data = JSON.parse(window.localStorage.getItem("paperworkSettings"));
  return (
    data || {
      defaultXPos: 0.64,
      defaultYPos: 0.04
    }
  );
}

export function setPaperworkSettings(data) {
  window.localStorage.setItem("paperworkSettings", JSON.stringify(data));
}

// any outstanding uploads / processing / uploading
export function shouldStopExit(state) {
  let anyLocal = Object.keys(state.localImages).length > 0;
  let anyProcessing =
    state.bulkModeProcessingFiles || state.processingFilesForCons.length > 0;
  let anyUploading = state.uploadingImages.length > 0;
  return anyLocal || anyProcessing || anyUploading;
}

// given a pwkId, give url to the jpg
export const getPaperworkURL = id => `/pwk/${id}.jpg`;

export async function getOutstandingUploads(dateFrom, dateTo, reqDep, delDep) {
  //gets a list of the outstanding paperwork to be uploaded
  let response = await axios.get("/api/paperworkReport", {
    params: { dateFrom, dateTo, reqDep, delDep }
  });
  return response.data;
}

// returns array of images mimicing <PaperworkViewer/> structure - grouped by con & duplicated
export function getDuplicatedPaperworkViewers(paperwork) {
  let conNos = paperwork.reduce((arr, { conNo }) => {
    return arr.includes(conNo) ? arr : [...arr, conNo];
  }, []);

  let pwk = paperwork.reduce((obj, pwk) => {
    obj[pwk.conNo] = obj[pwk.conNo] ? obj[pwk.conNo] : [];
    obj[pwk.conNo].push(pwk.id);
    return obj;
  }, {});

  let pwkImgData = paperwork.reduce((obj, pwk) => {
    let label = {
      x: pwk.xPos,
      y: pwk.yPos,
      conNo: pwk.conNo,
      despatchDate: pwk.despatchDate,
      barcode: pwk.barcode,
      colDep: pwk.colDep,
      delDep: pwk.delDep
    };
    obj[pwk.id] = getPaperworkImageForPrinting(pwk.id, label);
    return obj;
  }, {});

  let imgsInOrder = conNos.reduce((arr, conNo) => {
    let imgs = pwk[conNo].map(id => {
      return {
        id,
        imageData: pwkImgData[id],
        customerCopy: false
      };
    });
    let dupImgs = pwk[conNo].map(id => {
      return {
        id,
        imageData: pwkImgData[id],
        customerCopy: true
      };
    });
    return [...arr, ...imgs, ...dupImgs];
  }, []);

  let promises = imgsInOrder.map(x => x.imageData);
  return Promise.all(promises).then(imgs => {
    return imgs.map((imgData, i) => {
      let img = imgsInOrder[i]; // rely on index to get back to imgsInOrder data
      let className = "paperwork-viewer";
      if (img.customerCopy) {
        className += " customer-copy";
      }
      return (
        <div key={i} className={className}>
          <img src={imgData} className="paperwork-viewer-img" />
        </div>
      );
    });
  });
}

export function getPaperworkImageForPrinting(paperworkId, label) {
  let barcodeUrl = `/public/barcode.aspx?Barcode=${label.barcode}&size=25`;
  let logoUrl = "/images/pwkLogo.png";

  let pwkImageProm = urlToimage(getPaperworkURL(paperworkId));
  let barcodeImageProm = urlToimage(barcodeUrl);
  let logoImageProm = urlToimage(logoUrl);

  return Promise.all([
    pwkImageProm,
    barcodeImageProm,
    logoImageProm
  ]).then(([img, barcodeImage, logoImage]) => {
    return generatePaperworkImageForPrinting(
      img,
      label,
      barcodeImage,
      logoImage
    );
  });
}

export function generatePaperworkImageForPrinting(
  paperworkImage,
  label,
  barcodeImage,
  logoImage
) {
  //TODO - un-comment
  // if(paperworkImage.naturalWidth > paperworkImage.naturalHeight){
  //   throw new Error("paperwork image is expected to be portrait");
  // }

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const ppi = 300;
  const ppmm = ppi * 0.03937007874016; //1 pixel/inch = 0.03937007874016 dot/millimeter

  const paperworkDims = getPaperworkDims();

  let targetWidth = paperworkDims.page.width * ppmm;
  let targetHeight = paperworkDims.page.height * ppmm;

  let scaleW = targetWidth / paperworkImage.naturalWidth;
  let scaleH = targetHeight / paperworkImage.naturalHeight;

  //choose smallest scale (to fit)
  let scale = scaleW > scaleH ? scaleH : scaleW;

  // work out pixel values for pwk
  const width = paperworkImage.naturalWidth * scale;
  const height = paperworkImage.naturalHeight * scale;

  //resize canvas
  canvas.width = width;
  canvas.height = height;

  //TODO - remove
  const isLandscape =
    paperworkImage.naturalWidth > paperworkImage.naturalHeight;
  if (isLandscape) {
    canvas.width = height;
    canvas.height = width;
    ctx.save();
    ctx.translate(0, width);
    ctx.rotate(-90 * (Math.PI / 180));
  }

  //draw main image
  ctx.drawImage(paperworkImage, 0, 0, width, height);

  //TODO - remove
  if (isLandscape) {
    ctx.restore();
  }

  //Work out required label dimensions

  //pixel vals of bcode
  let bcodeWidth = paperworkDims.barcode.width * ppmm;
  let bcodeHeight = paperworkDims.barcode.height * ppmm;

  //other pixel vals
  let fontSize = paperworkDims.fontSize * ppmm;
  let padding = paperworkDims.padding * ppmm;
  let safeArea = paperworkDims.safeArea * ppmm;

  // pixel vals of logo
  let logoWidth = paperworkDims.logoWidth * ppmm;
  let logoHeight = padding * 2 + bcodeHeight + fontSize * 2;

  let outerWidth = paperworkDims.outerWidth * ppmm;
  let outerHeight = paperworkDims.outerHeight * ppmm;

  //pixel vals of label position (origin)
  let x = width * label.x;
  let y = height * label.y;

  const barcodeMarginPx = paperworkDims.barcodeMargin * ppmm;

  //dont allow label within margin
  if (x < barcodeMarginPx) {
    x = barcodeMarginPx;
  }
  if (x + outerWidth > width - barcodeMarginPx) {
    x = width - outerWidth - barcodeMarginPx;
  }
  if (y < barcodeMarginPx) {
    y = barcodeMarginPx;
  }
  if (y + outerHeight > height - barcodeMarginPx) {
    y = height - outerHeight - barcodeMarginPx;
  }

  //draw white label background & border
  let border = {
    width: 0.1 * ppmm, // 0.1mm
    style: "gray"
  };
  drawRectangle(ctx, x, y, outerWidth, outerHeight, "white", border);

  //barcode
  ctx.drawImage(
    barcodeImage,
    x + safeArea,
    y + padding * 2 + fontSize,
    bcodeWidth,
    bcodeHeight
  );

  //logo
  ctx.drawImage(
    logoImage,
    x + bcodeWidth + safeArea * 2,
    y + padding,
    logoWidth,
    logoHeight
  );

  // Text
  let tlText = `${label.colDep} (${format(label.despatchDate, "DD/MM/YY")})`;
  let trText = `Con No: ${label.conNo}`;
  let blText = label.barcode;
  let brText = label.delDep;

  let font = `${fontSize}px sans-serif`;
  let color = "black";
  //top Left
  writeText(
    ctx,
    tlText,
    x + safeArea,
    y + padding + fontSize,
    font,
    color,
    "start",
    "alphabetic"
  );
  //bottom left
  writeText(
    ctx,
    blText,
    x + safeArea,
    y + 3 * padding + fontSize + bcodeHeight,
    font,
    color,
    "start",
    "hanging"
  );
  //top right
  writeText(
    ctx,
    trText,
    x + bcodeWidth + safeArea,
    y + padding + fontSize,
    font,
    color,
    "end",
    "alphabetic"
  );
  //bott right
  writeText(
    ctx,
    brText,
    x + bcodeWidth + safeArea,
    y + 3 * padding + fontSize + bcodeHeight,
    font,
    color,
    "end",
    "hanging"
  );

  // render canvas as an image
  return canvas.toDataURL();
}
